import type { EvidenceStatement } from "../domain/types";

export type TruthAuditSeverity = "warning" | "blocking";

export const TruthAuditCode = {
  FACT_WITHOUT_EVIDENCE: "CEO-TRUTH-001",
  INFERENCE_WITHOUT_FACT: "CEO-TRUTH-002",
  HYPOTHESIS_WITHOUT_VERIFICATION: "CEO-TRUTH-003",
  HYPOTHESIS_WITHOUT_FALSIFICATION: "CEO-TRUTH-004",
  RECOMMENDATION_WITHOUT_BASIS: "CEO-TRUTH-005",
  ACTIVE_CONTRADICTION: "CEO-TRUTH-006",
  REVIEW_OVERDUE: "CEO-TRUTH-007",
  SUPERSEDED_STILL_ACTIVE: "CEO-TRUTH-008",
  HIGH_CONFIDENCE_LOW_EVIDENCE: "CEO-TRUTH-009",
  UNCONFIRMED_INFERENCE_EXPORT: "CEO-TRUTH-010",
  PRIVATE_SOURCE_IN_PROJECTION: "CEO-TRUTH-011",
  ASPIRATION_MARKED_AS_FACT: "CEO-TRUTH-012",
  SINGLE_RESULT_GENERALIZED: "CEO-TRUTH-013",
  VERBAL_APPROVAL_AS_PAYMENT: "CEO-TRUTH-014",
  ACTIVITY_AS_OUTCOME: "CEO-TRUTH-015",
} as const;

export type TruthAuditCode = (typeof TruthAuditCode)[keyof typeof TruthAuditCode];

export interface TruthAuditIssue {
  code: TruthAuditCode;
  severity: TruthAuditSeverity;
  statementIds: string[];
  path: string;
  rule: string;
  message: string;
}

export interface TruthAuditOptions {
  privateSourceIds?: ReadonlySet<string>;
  projectedStatementIds?: ReadonlySet<string>;
}

function issue(
  code: TruthAuditCode,
  severity: TruthAuditSeverity,
  statementIds: string[],
  path: string,
  rule: string,
  message: string,
): TruthAuditIssue {
  return { code, severity, statementIds, path, rule, message };
}

function isUserConfirmed(statement: EvidenceStatement): boolean {
  return statement.userConfirmation === "confirmed" || statement.userConfirmation === "corrected";
}

function hasBasis(statement: EvidenceStatement): boolean {
  if (statement.statementType !== "recommendation") return true;
  return statement.basedOnStatementIds.length > 0 || statement.principleIds.length > 0;
}

function activeStatements(statements: readonly EvidenceStatement[]): EvidenceStatement[] {
  return statements.filter(
    (statement) => statement.status === "active" && statement.deletionState === "active",
  );
}

export function auditTruthStatements(
  statements: readonly EvidenceStatement[],
  now = new Date().toISOString(),
  options: TruthAuditOptions = {},
): TruthAuditIssue[] {
  const issues: TruthAuditIssue[] = [];
  const active = activeStatements(statements);
  const activeById = new Map(active.map((statement) => [statement.id, statement]));
  const projectedIds = options.projectedStatementIds ?? new Set<string>();
  const privateIds = options.privateSourceIds ?? new Set<string>();
  const nowMs = new Date(now).getTime();

  for (const statement of statements) {
    if (statement.deletionState !== "active" || statement.status === "archived") continue;

    if (
      statement.statementType === "fact" &&
      statement.evidenceRefs.length === 0 &&
      !isUserConfirmed(statement)
    ) {
      issues.push(
        issue(
          TruthAuditCode.FACT_WITHOUT_EVIDENCE,
          "blocking",
          [statement.id],
          "evidenceRefs",
          "事实需要可观察证据或用户明确确认。",
          "该事实没有证据，且尚未由用户明确确认。",
        ),
      );
    }

    if (statement.statementType === "inference" && statement.supportingFactIds.length === 0) {
      issues.push(
        issue(
          TruthAuditCode.INFERENCE_WITHOUT_FACT,
          "blocking",
          [statement.id],
          "supportingFactIds",
          "推断必须引用至少一个支持事实。",
          "该推断没有引用支持事实。",
        ),
      );
    }

    if (statement.statementType === "hypothesis" && !statement.verificationCondition?.trim()) {
      issues.push(
        issue(
          TruthAuditCode.HYPOTHESIS_WITHOUT_VERIFICATION,
          "blocking",
          [statement.id],
          "verificationCondition",
          "假设必须说明什么结果可以验证它。",
          "该假设缺少验证条件。",
        ),
      );
    }

    if (statement.statementType === "hypothesis" && !statement.falsificationCondition?.trim()) {
      issues.push(
        issue(
          TruthAuditCode.HYPOTHESIS_WITHOUT_FALSIFICATION,
          "blocking",
          [statement.id],
          "falsificationCondition",
          "假设必须说明什么结果会使它失败。",
          "该假设缺少反证或失败条件。",
        ),
      );
    }

    if (statement.statementType === "recommendation" && !hasBasis(statement)) {
      issues.push(
        issue(
          TruthAuditCode.RECOMMENDATION_WITHOUT_BASIS,
          "blocking",
          [statement.id],
          "basedOnStatementIds",
          "建议必须引用事实、推断或共同原则。",
          "该建议没有可追溯依据。",
        ),
      );
    }

    if (statement.reviewDueAt && new Date(statement.reviewDueAt).getTime() < nowMs) {
      issues.push(
        issue(
          TruthAuditCode.REVIEW_OVERDUE,
          "warning",
          [statement.id],
          "reviewDueAt",
          "到期陈述需要重新检查，不能永久保持当前状态。",
          "该陈述已经超过最近复核日期。",
        ),
      );
    }

    if (
      statement.supersedesStatementId &&
      activeById.has(statement.supersedesStatementId) &&
      statement.status !== "draft"
    ) {
      issues.push(
        issue(
          TruthAuditCode.SUPERSEDED_STILL_ACTIVE,
          "warning",
          [statement.supersedesStatementId, statement.id],
          "supersedesStatementId",
          "被新信息取代的陈述不能继续保持 active。",
          "新陈述已经声明取代旧陈述，但旧陈述仍为 active。",
        ),
      );
    }

    const supportCount =
      statement.evidenceRefs.length +
      statement.supportsStatementIds.length +
      (statement.statementType === "inference" ? statement.supportingFactIds.length : 0);
    if ((statement.confidence ?? 0) >= 0.85 && supportCount === 0) {
      issues.push(
        issue(
          TruthAuditCode.HIGH_CONFIDENCE_LOW_EVIDENCE,
          "warning",
          [statement.id],
          "confidence",
          "高置信度不能替代证据。",
          "该陈述置信度很高，但没有证据或支持陈述。",
        ),
      );
    }

    if (
      statement.statementType === "inference" &&
      projectedIds.has(statement.id) &&
      !isUserConfirmed(statement)
    ) {
      issues.push(
        issue(
          TruthAuditCode.UNCONFIRMED_INFERENCE_EXPORT,
          "blocking",
          [statement.id],
          "userConfirmation",
          "未确认推断不得投影为外部事实。",
          "该未确认推断被选入了外部事实投影。",
        ),
      );
    }

    if (
      projectedIds.has(statement.id) &&
      (statement.sensitivity === "highly_sensitive" ||
        statement.sourceRecordIds.some((id) => privateIds.has(id)))
    ) {
      issues.push(
        issue(
          TruthAuditCode.PRIVATE_SOURCE_IN_PROJECTION,
          "blocking",
          [statement.id],
          "sourceRecordIds",
          "私人原文和高敏全文不得进入外部摘要。",
          "该外部投影包含私人来源引用或高敏全文。",
        ),
      );
    }

    if (
      statement.statementType === "fact" &&
      /(愿景|使命|目标是|希望|我们将|成为最|口号)/.test(statement.content)
    ) {
      issues.push(
        issue(
          TruthAuditCode.ASPIRATION_MARKED_AS_FACT,
          "warning",
          [statement.id],
          "statementType",
          "愿望、目标或口号不能只因用户选择就获得事实语义。",
          "该事实文本更像愿望、目标或口号，请重新核对类型。",
        ),
      );
    }

    if (
      /(一次|这次|单次)/.test(statement.content) &&
      /(总是|必然|长期规律|证明了|以后都会)/.test(statement.content)
    ) {
      issues.push(
        issue(
          TruthAuditCode.SINGLE_RESULT_GENERALIZED,
          "warning",
          [statement.id],
          "content",
          "一次结果不能直接泛化为长期规律。",
          "该陈述可能从一次结果推导了长期结论。",
        ),
      );
    }

    if (
      statement.statementType === "fact" &&
      /(口头|表达|认可|支持|感兴趣).{0,8}(付款|付费|订单|需求已验证)/.test(statement.content)
    ) {
      issues.push(
        issue(
          TruthAuditCode.VERBAL_APPROVAL_AS_PAYMENT,
          "warning",
          [statement.id],
          "content",
          "口头认可和付费行为是不同证据。",
          "该事实可能把口头认可标记为了付款或已验证需求。",
        ),
      );
    }

    if (
      statement.statementType === "fact" &&
      /(忙|加班|开会|制作|开发|研究|阅读)/.test(statement.content) &&
      /(成果|增长|验证成功|经营结果)/.test(statement.content) &&
      !/(付款|复购|签约|交付完成|收入|客户回复)/.test(statement.content)
    ) {
      issues.push(
        issue(
          TruthAuditCode.ACTIVITY_AS_OUTCOME,
          "warning",
          [statement.id],
          "content",
          "繁忙活动不能直接替代可观察经营结果。",
          "该陈述可能把活动量标记成经营结果。",
        ),
      );
    }
  }

  const seenPairs = new Set<string>();
  for (const statement of active) {
    for (const otherId of statement.contradictsStatementIds) {
      const other = activeById.get(otherId);
      if (!other) continue;
      const pair = [statement.id, other.id].sort().join(":");
      if (seenPairs.has(pair)) continue;
      seenPairs.add(pair);
      issues.push(
        issue(
          TruthAuditCode.ACTIVE_CONTRADICTION,
          "blocking",
          [statement.id, other.id],
          "contradictsStatementIds",
          "明确互相矛盾的陈述不能同时保持 active 而不提示用户。",
          "两条 active 陈述被明确标记为互相矛盾。",
        ),
      );
    }
  }

  return issues.sort((left, right) => {
    if (left.severity !== right.severity) return left.severity === "blocking" ? -1 : 1;
    return left.code.localeCompare(right.code);
  });
}
